// src/services/analytics.service.js
//
// Google Ads conversion tag + Consent Mode v2. Every export is a silent
// no-op when VUE_APP_GOOGLE_ADS_ID is unset (dev, staging, tests), and
// no function here may ever throw into the booking/payment path.

const CONSENT_KEY = "crescendo_consent";

const ADS_ID = process.env.VUE_APP_GOOGLE_ADS_ID;
const CONVERSION_LABEL = process.env.VUE_APP_GOOGLE_ADS_CONVERSION_LABEL;

export function isAnalyticsConfigured() {
  return Boolean(ADS_ID);
}

// Pages that must carry no Google tag and no cookie banner. /lessons is the
// landing page for the paid-search test: it is measured with Plausible
// (cookieless) and Firestore, and promises visitors nothing is tracked.
const TAG_FREE_PATHS = ["/lessons"];

export function isTagFreePath(pathname) {
  const path = (pathname || "").replace(/\/+$/, "") || "/";
  return TAG_FREE_PATHS.includes(path);
}

export function getStoredConsent() {
  try {
    const raw = window.localStorage.getItem(CONSENT_KEY);
    return raw === "granted" || raw === "denied" ? raw : null;
  } catch (err) {
    return null;
  }
}

// Standard gtag shim: gtag.js drains window.dataLayer when it loads, so
// calls queued before (and without) the script still work.
function gtag() {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(arguments);
}

const applyConsent = (granted) => {
  const value = granted ? "granted" : "denied";
  gtag("consent", "update", {
    ad_storage: value,
    ad_user_data: value,
    ad_personalization: value,
    analytics_storage: value,
  });
};

export function initAnalytics() {
  try {
    if (!isAnalyticsConfigured()) return;
    // Landing on a tag-free page skips gtag for the whole SPA session.
    if (isTagFreePath(window.location.pathname)) return;

    // Consent defaults must be queued before gtag.js loads.
    gtag("consent", "default", {
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
      analytics_storage: "denied",
    });
    gtag("set", "url_passthrough", true);
    gtag("set", "ads_data_redaction", true);

    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${ADS_ID}`;
    script.setAttribute("data-analytics", "gtag");
    document.head.appendChild(script);

    gtag("js", new Date());
    gtag("config", ADS_ID);

    const stored = getStoredConsent();
    if (stored !== null) applyConsent(stored === "granted");
  } catch (err) {
    // Analytics must never break app boot.
  }
}

export function updateConsent(granted) {
  try {
    if (!isAnalyticsConfigured()) return;
    applyConsent(granted);
    window.localStorage.setItem(CONSENT_KEY, granted ? "granted" : "denied");
  } catch (err) {
    // Consent persistence failure is not worth surfacing.
  }
}

export function trackConversion({ value, transactionId }) {
  try {
    if (!isAnalyticsConfigured() || !CONVERSION_LABEL) return;
    gtag("event", "conversion", {
      send_to: `${ADS_ID}/${CONVERSION_LABEL}`,
      value,
      currency: "GBP",
      transaction_id: transactionId,
    });
  } catch (err) {
    // Tracking must never affect the payment flow.
  }
}
